import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from "react";
import {
  Plus,
  CheckCircle2,
  Send,
  Clock,
  Sparkles,
  AlertTriangle
} from "lucide-react";
import {
  getBatches,
  getBatchesPortal,
  updateBatchStatus,
  updateBatchStatusPortal,
} from "../../api/fees/feeApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import GenerateBatchModal from "../../components/fees/GenerateBatchModal";
import {
  Button,
  Select,
  Table,
  Badge,
  Alert,
  Tooltip,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

// ── DEBUG HELPERS ────────────────────────────────────────────────────────────
// Pretty-prints the `details` object returned on generation failure
// ({ missingFeeStructures, duplicateSkipCount, transportHeadsSkipped }) or the
// `warnings` object returned on partial-success, so you can see exactly which
// class/fee-head combination is the problem without opening devtools.
const DebugPanel = ({
  title,
  tone = "danger",
  details
}) => {
  if (!details) return null;

  const missing = details.missingFeeStructures || [];
  const duplicateSkipCount = details.duplicateSkipCount;
  const transportHeadsSkipped = details.transportHeadsSkipped || [];

  return (
    <Alert variant={tone} title={title} className="mb-4">
      <div className="text-xs">
        {missing.length > 0 && (
          <div className="mb-2">
            <div className="font-medium">Missing fee structures ({missing.length}):</div>
            <ul className="list-disc list-inside">
              {missing.map((m, i) => (
                <li key={i}>
                  classId=<code>{m.classId}</code> feeHeadId=<code>{m.feeHeadId}</code>{" "}
                  {m.feeHeadName ? `(${m.feeHeadName})` : ""}
                </li>
              ))}
            </ul>
            <div className="mt-1 opacity-80">
              → No active <code>FeeStructure</code> exists for this exact class + fee-head pair.
              Create/activate one for each row above.
            </div>
          </div>
        )}

        {typeof duplicateSkipCount === "number" && duplicateSkipCount > 0 && (
          <div className="mb-2">
            <span className="font-medium">Duplicate skip count:</span> {duplicateSkipCount}
            <div className="opacity-80">
              → These students already have a ledger entry for the selected fee head(s).
            </div>
          </div>
        )}

        {transportHeadsSkipped.length > 0 && (
          <div className="mb-2">
            <span className="font-medium">Transport-module heads skipped:</span>{" "}
            {transportHeadsSkipped.join(", ")}
            <div className="opacity-80">
              → These fee heads are handled by the Transport module, not batch generation.
            </div>
          </div>
        )}

        <details className="mt-2">
          <summary className="cursor-pointer select-none opacity-70">Raw JSON</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all rounded border bg-white/60 p-2">
            {JSON.stringify(details, null, 2)}
          </pre>
        </details>
      </div>
    </Alert>
  );
};
const statusBadgeVariant = {
  DRAFT: "default",
  APPROVED: "warning",
  PUBLISHED: "success",
};
const FeeBatchList = ({
    isPortal = false,
    canManage = true
  }) => {
    const [batches, setBatches] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [filters, setFilters] = useState({
      academicYearId: "",
      status: ""
    });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const toast = useToast();
    // DEBUG: last generation error's `details` (missingFeeStructures / duplicateSkipCount / transportHeadsSkipped)
    const [lastErrorDetails, setLastErrorDetails] = useState(null);
    // DEBUG: last generation success's `warnings` (partial mismatches even though batch was created)
    const [lastWarnings, setLastWarnings] = useState(null);

    useEffect(() => {
      (isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears())
      .then((res) => {
          const years = res?.data || res || [];
          setAcademicYears(Array.isArray(years) ? years : []);
        })
        .catch(() => {});
    }, [isPortal]);
    const fetchData = useCallback(async () => {
      setLoading(true);
      try {
        const active = Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "")
        );
        const res = isPortal ? await getBatchesPortal(active) : await getBatches(active);
        setBatches(res.data || res || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Unable to load batches.");
      } finally {
        setLoading(false);
      }
    }, [filters, isPortal]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);
    const changeStatus = async (id, status) => {
      setUpdatingId(id);
      try {
        if (isPortal) await updateBatchStatusPortal(id, {
          status
        });
        else await updateBatchStatus(id, {
          status
        });
        toast.success(`Batch ${status.toLowerCase()}.`);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.message || "Status update failed.");
        // DEBUG: status-change failures don't carry the same `details` shape,
        // but log the raw response in case the backend adds diagnostics here too.
        // eslint-disable-next-line no-console
        console.debug("[FeeBatchList] status change error:", err.response?.data);
      } finally {
        setUpdatingId(null);
      }
    };

    // DEBUG: called by GenerateBatchModal on a FAILED generate attempt.
    // Expects the modal to pass the axios error's response body, e.g.
    // catch (err) { onGenerateError?.(err.response?.data); }
    const handleGenerateError = (errBody) => {
      setLastWarnings(null);
      toast.error(errBody?.message || "Batch generation failed.");
      setLastErrorDetails(errBody?.details || null);
      // eslint-disable-next-line no-console
      console.debug("[FeeBatchList] generate batch error payload:", errBody);
    };

    // DEBUG: called by GenerateBatchModal on a SUCCESSFUL generate attempt.
    // Expects the modal to pass the full response payload, e.g.
    // onSuccess(response) where response = await generateFeeBatch(data)
    const handleGenerateSuccess = (response) => {
      setLastErrorDetails(null);
      const studentsCount = response?.data?.batch?.totalStudentsCount;
      const amount = response?.data?.batch?.totalAmountGenerated;
      const warnings = response?.warnings || null;
      setLastWarnings(warnings);
      toast.success(
        studentsCount !== undefined
          ? `Batch generated as DRAFT — ${studentsCount} student(s), ₹${Number(amount || 0).toFixed(2)}.`
          : "Batch generated as DRAFT."
      );
      // eslint-disable-next-line no-console
      console.debug("[FeeBatchList] generate batch success payload:", response);
      fetchData();
    };

    // ---- Presentation-only derivations below (no data/logic changes) ----
    const academicYearOptions = useMemo(
      () => [
        { label: "All Academic Years", value: "" },
        ...academicYears.map((y) => ({ label: y.yearName, value: y._id })),
      ],
      [academicYears]
    );
    const statusOptions = [
      { label: "All Statuses", value: "" },
      { label: "DRAFT", value: "DRAFT" },
      { label: "APPROVED", value: "APPROVED" },
      { label: "PUBLISHED", value: "PUBLISHED" },
    ];
    const tableRows = useMemo(
      () => batches.map((b) => ({ ...b, id: b._id })),
      [batches]
    );
    const columns = [
      {
        key: "batchName",
        header: "Batch",
        render: (b) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {b.batchName}
            </span>
            {b.source === "AUTO_SCHEDULER" && (
              <Badge variant="primary" className="gap-1">
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                Auto scheduler
              </Badge>
            )}
            {/* DEBUG: flag zero-count batches so they're easy to spot */}
            {b.status === "DRAFT" && (b.totalStudentsCount || 0) === 0 && (
              <Tooltip content="0 students / 0 amount — likely a missing fee structure for the selected class(es).">
                <span>
                  <Badge variant="danger" className="gap-1">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    EMPTY
                  </Badge>
                </span>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
  key: "monthYear",
  header: "Month / Year",
  render: (b) => `${b.month} / ${b.year}`,
}, {
  key: "dueDate",
  header: "Due Date",
  render: (b) => (b.dueDate ? new Date(b.dueDate).toLocaleDateString("en-IN") : "—"),
}, {
  key: "totalStudentsCount",
  header: "Students",
  align: "right",
  render: (b) => b.totalStudentsCount || 0,
}, {
  key: "totalAmountGenerated",
  header: "Amount",
  align: "right",
  render: (b) => `₹${Number(b.totalAmountGenerated || 0).toFixed(2)}`,
}, {
  key: "status",
  header: "Status",
  render: (b) => (
    <span className="inline-flex items-center gap-1">
          <Badge variant={statusBadgeVariant[b.status] || "default"}>{b.status}</Badge>
          {b.needsOwnerApproval && b.status === "DRAFT" && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">· needs approval</span>
          )}
        </span>
  ),
},
...(canManage ?
  [{
    key: "actions",
    header: "Actions",
    align: "right",
    nowrap: true,
    render: (b) => (
      <div className="flex flex-nowrap items-center justify-end gap-1.5">
                {/* Owner only: approve DRAFT */}
                {b.status === "DRAFT" && !isPortal && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    isLoading={updatingId === b._id}
                    leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                    onClick={() => changeStatus(b._id, "APPROVED")}
                    className="bg-warning-50 text-warning-700 hover:bg-warning-100"
                  >
                    Approve
                  </Button>
                )}
                {b.status === "DRAFT" && isPortal && (
                  <Tooltip content="Waiting for owner/principal approval">
                    <span>
                      <Badge variant="warning" className="gap-1">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        Pending approval
                      </Badge>
                    </span>
                  </Tooltip>
                )}
                {/* Publish after approval (owner or staff) */}
                {b.status === "APPROVED" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    isLoading={updatingId === b._id}
                    leftIcon={<Send className="h-3.5 w-3.5" />}
                    onClick={() => changeStatus(b._id, "PUBLISHED")}
                    className="bg-success-50 text-success-700 hover:bg-success-100"
                  >
                    Publish
                  </Button>
                )}
              </div>
    ),
  }, ] :
  []),
];

return (
  <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Fee Batches</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Generate, approve and publish fee batches</p>
        </div>
        {canManage && (
          <Button type="button" variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            Generate Batch
          </Button>
        )}
      </div>

      
      {/* DEBUG: detailed breakdown of why generation failed */}
      <DebugPanel title="Debug: why generation failed" tone="danger" details={lastErrorDetails} />

      
      {/* DEBUG: batch was created, but some class/fee-head pairs were silently skipped */}
      <DebugPanel title="Debug: batch created with warnings" tone="warning" details={lastWarnings} />

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="w-full sm:w-56">
          <Select
            options={academicYearOptions}
            value={filters.academicYearId}
            onChange={(val) => setFilters((p) => ({ ...p, academicYearId: val }))}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            options={statusOptions}
            value={filters.status}
            onChange={(val) => setFilters((p) => ({ ...p, status: val }))}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={tableRows}
        isLoading={loading}
        emptyState={<EmptyState title="No records found" description="Try adjusting your filters or generate a new batch." />}
      />

      <GenerateBatchModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        // DEBUG: onSuccess now expects the full API response payload
        // (i.e. `response` from `await generateFeeBatch(data)`), not no-args.
        // Update GenerateBatchModal's success handler from `onSuccess()` to
        // `onSuccess(response)`.
        onSuccess={handleGenerateSuccess}
        // DEBUG: new prop — call this from GenerateBatchModal's catch block with
        // `err.response?.data` so this list can render the `details` breakdown.
        //   catch (err) { onGenerateError?.(err.response?.data); }
        onGenerateError={handleGenerateError}
        isPortal={isPortal}
      />
    </div>
);
};

export default FeeBatchList;
